/**
 * affiliate-settings-page.tsx — Platform → Tiếp thị liên kết → Cài đặt chương trình. Route `/platform/affiliate/settings`.
 * Mọi quy tắc do super-admin đặt (lưu platform_settings `affiliate_config`, mỗi lần lưu tăng phiên bản + ghi nhật ký).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Loader2, Plus, Save, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import { programSummary, sortTiers, validateConfig, type AffiliateConfig } from '@/lib/affiliate'
import { useAffiliateConfig, useSaveAffiliateConfig } from '@/hooks/use-platform-affiliate'

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Card className="space-y-4 p-5">
      <div>
        <p className="font-medium">{title}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </Card>
  )
}

function Num({ label, value, onChange, suffix, step, hint }: { label: string; value: number; onChange: (n: number) => void; suffix?: string; step?: number; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Input type="number" step={step ?? 1} value={Number.isFinite(value) ? value : ''} onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} />
        {suffix && <span className="shrink-0 text-sm text-muted-foreground">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function Choice<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={cn('rounded-md border px-3 py-1.5 text-sm transition-colors', value === v ? 'border-primary bg-primary/10 text-primary' : 'hover:border-primary/40')}>
          {l}
        </button>
      ))}
    </div>
  )
}

export function AffiliateSettingsPage() {
  const { data, isLoading, isError } = useAffiliateConfig()
  const save = useSaveAffiliateConfig()
  const [c, setC] = useState<AffiliateConfig | null>(null)
  useEffect(() => { if (data) setC(data.config) }, [data])

  if (isLoading || (!c && !isError)) return <Loading />
  if (isError || !c || !data) return <ErrorState />

  const up = (fn: (d: AffiliateConfig) => AffiliateConfig) => setC((d) => (d ? fn(d) : d))
  const cm = (patch: Partial<AffiliateConfig['commission']>) => up((d) => ({ ...d, commission: { ...d.commission, ...patch } }))
  const err = validateConfig(c)

  const onSave = () => {
    if (err) { toast.error(err); return }
    save.mutate({ ...c, commission: { ...c.commission, tiers: sortTiers(c.commission.tiers) } }, {
      onSuccess: () => toast.success('Đã lưu cấu hình (phiên bản mới)'),
      onError: (e) => toast.error(platformApiError(e)),
    })
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Cài đặt chương trình"
        description={`Phiên bản ${data.config.version}${data.isDefault ? ' (mặc định, chưa lưu)' : ''}. Khách đã gắn giữ thời hạn / phạm vi lúc gắn; tỷ lệ, bậc, thời gian giữ mới áp cho hoa hồng phát sinh sau khi lưu.`}
        actions={
          <Button onClick={onSave} disabled={save.isPending || !!err}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lưu cấu hình
          </Button>
        }
      />
      {err && <p className="text-sm text-destructive">{err}</p>}
      <Card className="p-4 text-sm">{programSummary(c)}</Card>

      <Section title="Trạng thái">
        <label className="flex items-center gap-3 text-sm">
          <Switch checked={c.enabled} onCheckedChange={(v) => up((d) => ({ ...d, enabled: v }))} />
          {c.enabled ? 'Đang bật — nhận đăng ký đối tác mới' : 'Đang tắt — CHỈ ngừng nhận đăng ký đối tác mới; đối tác hiện có vẫn hoạt động bình thường (link, mã, hoa hồng, rút tiền)'}
        </label>
      </Section>

      <Section title="Hoa hồng" description="Cơ sở tính = số tiền khách thực trả TRƯỚC VAT, sau mã ưu đãi; đổi gói / đổi số lượng chỉ tính phần chênh thực trả; mua thêm có tính; đơn 0đ không tính.">
        <div className="space-y-2">
          <Label>Loại</Label>
          <Choice value={c.commission.type} onChange={(v) => cm({ type: v })} options={[['percent', '% giá trị đơn'], ['fixed', 'Số tiền cố định / đơn']]} />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {c.commission.type === 'percent'
            ? <Num label="Tỷ lệ cơ bản" value={c.commission.percent} step={0.1} suffix="%" onChange={(n) => cm({ percent: n })} />
            : <Num label="Số tiền cơ bản / đơn" value={c.commission.fixedAmount} suffix="đ" onChange={(n) => cm({ fixedAmount: n })} />}
          <Num label="Thời gian hưởng" value={c.commission.durationDays} suffix="ngày" hint="Tính từ ngày gắn khách. 0 = không giới hạn. Đơn trả trong thời gian này được tính toàn bộ (kể cả trả trước nhiều năm)." onChange={(n) => cm({ durationDays: n })} />
          <div className="space-y-1.5">
            <Label>Đơn được tính</Label>
            <Choice value={c.commission.scope} onChange={(v) => cm({ scope: v })} options={[['recurring', 'Mọi đơn (định kỳ)'], ['first_order', 'Chỉ đơn đầu']]} />
            <p className="text-xs text-muted-foreground">"Chỉ đơn đầu" = đơn trả tiền &gt; 0 đầu tiên sau khi gắn khách.</p>
          </div>
          <Num label="Trần hoa hồng mỗi khách (0 = không giới hạn)" suffix="đ" value={c.commission.capPerCustomer ?? 0}
            hint="Tổng hoa hồng (chưa thu hồi) của một khách không vượt trần; đơn chạm trần nhận phần còn lại. Hoa hồng cố định không vượt tiền đơn trước VAT."
            onChange={(n) => cm({ capPerCustomer: n > 0 ? n : null })} />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Bậc hoa hồng (tuỳ chọn)</Label>
            <Button size="sm" variant="outline" onClick={() => cm({ tiers: [...c.commission.tiers, { name: `Bậc ${c.commission.tiers.length + 1}`, minRevenue: 0, minPayingCustomers: 1, value: c.commission.type === 'percent' ? c.commission.percent : c.commission.fixedAmount }] })}>
              <Plus className="h-3.5 w-3.5" /> Thêm bậc
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Đạt MỌI điều kiện khác 0 của bậc → áp mức của bậc (bậc có mức cao nhất thắng). Doanh thu = tổng cơ sở tính (trước VAT) luỹ kế của các khách đối tác đã giới thiệu, gồm đơn đang tính.
          </p>
          {c.commission.tiers.map((t, i) => (
            <div key={i} className="grid items-end gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              <div className="space-y-1.5"><Label>Tên bậc</Label><Input value={t.name} onChange={(e) => cm({ tiers: c.commission.tiers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} /></div>
              <Num label="Doanh thu tối thiểu" suffix="đ" value={t.minRevenue} onChange={(n) => cm({ tiers: c.commission.tiers.map((x, j) => (j === i ? { ...x, minRevenue: n } : x)) })} />
              <Num label="Số khách trả tiền tối thiểu" value={t.minPayingCustomers} onChange={(n) => cm({ tiers: c.commission.tiers.map((x, j) => (j === i ? { ...x, minPayingCustomers: n } : x)) })} />
              <Num label={c.commission.type === 'percent' ? 'Tỷ lệ' : 'Số tiền / đơn'} suffix={c.commission.type === 'percent' ? '%' : 'đ'} step={c.commission.type === 'percent' ? 0.1 : 1000}
                value={t.value} onChange={(n) => cm({ tiers: c.commission.tiers.map((x, j) => (j === i ? { ...x, value: n } : x)) })} />
              <Button size="icon" variant="ghost" aria-label="Xoá bậc" onClick={() => cm({ tiers: c.commission.tiers.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label>Nhà cung cấp thanh toán không tính hoa hồng</Label>
          <Input value={c.commission.excludeProviders.join(', ')} onChange={(e) => cm({ excludeProviders: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
          <p className="text-xs text-muted-foreground">Mặc định "manual" (Platform gia hạn tay). Đơn quy đổi / mã 100% luôn 0đ nên tự bỏ qua.</p>
        </div>
      </Section>

      <Section title="Ghi nhận khách" description="Link giới thiệu nằm trên tên miền hệ thống: https://<hệ thống>/r/<mã>. Tổ chức được chốt người giới thiệu sau đơn thanh toán đầu tiên.">
        <div className="grid gap-3 sm:grid-cols-3">
          <Num label="Hiệu lực link (cookie)" suffix="ngày" value={c.cookieDays} onChange={(n) => up((d) => ({ ...d, cookieDays: n }))} />
          <Num label="Chủ tổ chức tự nhập mã trong" suffix="ngày" hint="Kể từ khi tạo tổ chức; 0 = tắt" value={c.bindWindowDays} onChange={(n) => up((d) => ({ ...d, bindWindowDays: n }))} />
          <div className="space-y-1.5">
            <Label>Mô hình ghi nhận</Label>
            <Choice value={c.attributionModel} onChange={(v) => up((d) => ({ ...d, attributionModel: v }))} options={[['first_touch', 'Người đầu tiên'], ['last_touch', 'Người gần nhất']]} />
          </div>
        </div>
      </Section>

      <Section title="Giữ & rút tiền">
        <div className="grid gap-3 sm:grid-cols-3">
          <Num label="Thời gian giữ" suffix="ngày" hint="Sau khi đơn được trả, trước khi hoa hồng rút được" value={c.holdDays} onChange={(n) => up((d) => ({ ...d, holdDays: n }))} />
          <Num label="Rút tối thiểu" suffix="đ" value={c.minWithdrawal} onChange={(n) => up((d) => ({ ...d, minWithdrawal: n }))} />
        </div>
      </Section>

      <Section title="Ưu đãi cho khách được giới thiệu" description="Bật → mã của mỗi đối tác đồng thời là mã ưu đãi (tạo tự động ở Mã ưu đãi). Khách dùng mã khi thanh toán vừa được giảm vừa được ghi nhận cho đối tác. Tổ chức của chính đối tác không dùng được mã.">
        <Choice value={c.customerDiscount.mode} onChange={(v) => up((d) => ({ ...d, customerDiscount: { ...d.customerDiscount, mode: v, value: v === 'percent' ? Math.min(100, Math.max(1, d.customerDiscount.value || 10)) : v === 'fixed' ? Math.max(1000, d.customerDiscount.value) : d.customerDiscount.value } }))}
          options={[['off', 'Tắt'], ['percent', 'Giảm %'], ['fixed', 'Giảm số tiền']]} />
        {c.customerDiscount.mode !== 'off' && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Num label={c.customerDiscount.mode === 'percent' ? 'Phần trăm giảm' : 'Số tiền giảm'} suffix={c.customerDiscount.mode === 'percent' ? '%' : 'đ'}
              value={c.customerDiscount.value} onChange={(n) => up((d) => ({ ...d, customerDiscount: { ...d.customerDiscount, value: n } }))} />
            {c.customerDiscount.mode === 'percent' && (
              <Num label="Giảm tối đa (0 = không trần)" suffix="đ" value={c.customerDiscount.maxDiscountAmount ?? 0}
                onChange={(n) => up((d) => ({ ...d, customerDiscount: { ...d.customerDiscount, maxDiscountAmount: n > 0 ? n : null } }))} />
            )}
            <Num label="Áp cho số lần mua đầu" hint="1 = chỉ lần mua đầu tiên" value={c.customerDiscount.purchases}
              onChange={(n) => up((d) => ({ ...d, customerDiscount: { ...d.customerDiscount, purchases: Math.max(1, n) } }))} />
          </div>
        )}
      </Section>

      <Section title="Thuế TNCN khi chi" description="Quy tắc phải theo luật hiện hành và được kế toán xác nhận. Thuế tính lúc duyệt lệnh rút, hiển thị trên lệnh và file xuất.">
        <Choice value={c.tax.mode} onChange={(v) => up((d) => ({ ...d, tax: { ...d.tax, mode: v } }))}
          options={[['withhold_individual', 'Khấu trừ với cá nhân'], ['none', 'Không khấu trừ']]} />
        {c.tax.mode === 'withhold_individual' && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Num label="Thuế suất" suffix="%" step={0.1} value={c.tax.ratePercent} onChange={(n) => up((d) => ({ ...d, tax: { ...d.tax, ratePercent: n } }))} />
              <Num label="Khi một lần chi từ" suffix="đ" value={c.tax.thresholdPerPayment} onChange={(n) => up((d) => ({ ...d, tax: { ...d.tax, thresholdPerPayment: n } }))} />
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Switch checked={c.tax.allowCommitment08} onCheckedChange={(v) => up((d) => ({ ...d, tax: { ...d.tax, allowCommitment08: v } }))} />
              Không khấu trừ với cá nhân có cam kết mẫu 08/CK-TNCN
            </label>
          </>
        )}
        <label className="flex items-center gap-3 text-sm">
          <Switch checked={c.tax.companyRequiresInvoice} onCheckedChange={(v) => up((d) => ({ ...d, tax: { ...d.tax, companyRequiresInvoice: v } }))} />
          Đối tác doanh nghiệp: không khấu trừ, yêu cầu xuất hoá đơn cho hoa hồng
        </label>
      </Section>

      <Section title="Bên chi trả trên chứng từ thuế TNCN" description="In trên chứng từ thu nhập / khấu trừ thuế TNCN gửi đối tác. Bỏ trống tên → dùng tên thương hiệu.">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5"><Label>Tên tổ chức</Label><Input value={c.payer.name} maxLength={200} onChange={(e) => up((d) => ({ ...d, payer: { ...d.payer, name: e.target.value } }))} /></div>
          <div className="space-y-1.5"><Label>Mã số thuế</Label><Input value={c.payer.taxCode} maxLength={20} onChange={(e) => up((d) => ({ ...d, payer: { ...d.payer, taxCode: e.target.value } }))} /></div>
          <div className="space-y-1.5"><Label>Địa chỉ</Label><Input value={c.payer.address} maxLength={300} onChange={(e) => up((d) => ({ ...d, payer: { ...d.payer, address: e.target.value } }))} /></div>
        </div>
      </Section>

      <Section title="VAT dùng để tách tiền trước thuế" description={`Đang áp: giá ${data.effectiveVat.includesVat ? 'ĐÃ gồm' : 'CHƯA gồm'} VAT ${data.effectiveVat.ratePercent}% (theo cấu hình đã lưu).`}>
        <Choice value={c.vat.source} onChange={(v) => up((d) => ({ ...d, vat: { ...d.vat, source: v } }))}
          options={[['einvoice', 'Theo cấu hình hoá đơn điện tử'], ['manual', 'Đặt tay']]} />
        {c.vat.source === 'manual' && (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Giá bán</Label>
              <Choice value={c.vat.includesVat ? 'incl' : 'excl'} onChange={(v) => up((d) => ({ ...d, vat: { ...d.vat, includesVat: v === 'incl' } }))} options={[['incl', 'Đã gồm VAT'], ['excl', 'Chưa gồm VAT']]} />
            </div>
            <Num label="Thuế suất VAT" suffix="%" value={c.vat.ratePercent} onChange={(n) => up((d) => ({ ...d, vat: { ...d.vat, ratePercent: n } }))} />
          </div>
        )}
      </Section>

      <Section title="Điều khoản chương trình" description="Hiển thị cho người dùng khi đăng ký đối tác.">
        <Textarea rows={8} value={c.terms} onChange={(e) => up((d) => ({ ...d, terms: e.target.value }))} maxLength={20000} />
      </Section>
    </div>
  )
}
